'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { useTranslation } from '@/lib/locales';
interface DirectionCardProps {
  image?: string;
  fallbackGradient: string;
  title: React.ReactNode;
  subtitle: string;
  href?: string;
  linkLabel: string;
  linkIcon?: 'right' | 'down';
}
function DirectionCard(t0) {
  const $ = _c(25);
  const {
    image,
    fallbackGradient,
    title,
    subtitle,
    href,
    linkLabel,
    linkIcon: t1
  } = t0;
  const linkIcon = t1 === undefined ? "right" : t1;
  const Icon = linkIcon === "down" ? ArrowDown : ArrowRight;
  const t2 = `relative aspect-[4/3] overflow-hidden ${fallbackGradient}`;
  let t3;
  if ($[0] !== image) {
    t3 = image && <img src={image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" onError={_temp} />;
    $[0] = image;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== title) {
    t5 = <h3 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">{title}</h3>;
    $[5] = title;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== subtitle) {
    t6 = <p className="mb-4 text-sm leading-relaxed text-gray-500">{subtitle}</p>;
    $[7] = subtitle;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `h-4 w-4 transition-transform ${linkIcon === "down" ? "group-hover:translate-y-0.5" : "group-hover:translate-x-0.5"}`;
  let t8;
  if ($[9] !== Icon || $[10] !== t7) {
    t8 = <Icon className={t7} />;
    $[9] = Icon;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== linkLabel || $[13] !== t8) {
    t9 = <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 group-hover:underline">{linkLabel}{t8}</span>;
    $[12] = linkLabel;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t9) {
    t10 = <div className="flex flex-1 flex-col items-center p-6 text-center">{t5}{t6}{t9}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t4) {
    t11 = <div className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl">{t4}{t10}</div>;
    $[19] = t10;
    $[20] = t4;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const content = t11;
  if (href) {
    let t12;
    if ($[22] !== content || $[23] !== href) {
      t12 = <Link href={href}>{content}</Link>;
      $[22] = content;
      $[23] = href;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    return t12;
  }
  return content;
}
function _temp(e) {
  (e.target as HTMLImageElement).style.display = "none";
}
export function DirectToRightPlaceV3() {
  const $ = _c(17);
  const {
    t
  } = useTranslation();
  const scrollToContent = _temp2;
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="mb-3 text-center text-sm font-semibold uppercase tracking-[0.2em] text-primary-300">One mission, different roles</p>;
    t1 = <h2 className="mb-4 text-center text-3xl font-extrabold text-white sm:text-4xl md:text-5xl">How do you want to use Sizzle today?</h2>;
    t2 = <p className="mx-auto mb-12 max-w-3xl text-center text-lg text-gray-300">Whether you're here to discover something new for yourself, grow a product, or help create the places and moments where discovery happens, we'll point you in the right direction.</p>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DirectionCard fallbackGradient="bg-gradient-to-br from-orange-200 to-orange-400" image="https://plus.unsplash.com/premium_photo-1761846736887-49c35b17b1b6?w=600&h=450&fit=crop" href="/for-me" title={<span>I want to{" "}<span className="underline decoration-primary-500 decoration-3 underline-offset-2">discover, try, and enjoy</span></span>} subtitle="Show me offers, tastings, events, and new products worth trying." linkLabel="Explore for me" linkIcon="right" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="underline decoration-primary-500 decoration-3 underline-offset-2">make</span>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t) {
    t5 = t("industry");
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <span>I{" "}{t4}{" "}{t5} products</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t) {
    t7 = t("industry");
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = `Help me create more discovery, trial, and repeat for my ${t7} products.`;
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <div onClick={scrollToContent}><DirectionCard fallbackGradient="bg-gradient-to-br from-purple-200 to-purple-400" image="https://plus.unsplash.com/premium_photo-1682144509812-4734c830b486?w=600&h=450&fit=crop" title={t6} subtitle={t8} linkLabel="See brand paths" linkIcon="down" /></div>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div onClick={scrollToContent}><DirectionCard fallbackGradient="bg-gradient-to-br from-blue-200 to-blue-400" image="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=600&h=450&fit=crop" title={<span>I{" "}<span className="underline decoration-primary-500 decoration-3 underline-offset-2">sell, source, or host</span>{" "}great products</span>} subtitle="Help me bring better discovery experiences to my store, venue, or audience." linkLabel="See business paths" linkIcon="down" /></div>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t9) {
    t11 = <section id="home-v3-role-chooser" className="bg-gradient-to-b from-gray-900 to-slate-900 py-16 lg:py-24"><div className="container mx-auto px-4 sm:px-6 lg:px-8">{t0}{t1}{t2}<div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-3">{t3}{t9}{t10}</div></div></section>;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}
function _temp2() {
  const el = document.getElementById("sizzle-for-section");
  el?.scrollIntoView({
    behavior: "smooth"
  });
}